
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Lock, Crown, Zap, Shield } from 'lucide-react';
import { useEnhancedSubscription } from '@/hooks/useEnhancedSubscription';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { cn } from '@/lib/utils';

interface SubscriptionAwareSidebarItemProps {
  item: any;
  isActive: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const TIER_REQUIREMENTS: Record<string, string> = {
  '/unified-calculator': 'pro',
  '/smartbuy-analyzer': 'pro',
  '/buying-power': 'pro',
  '/investor-analytics': 'investor',
  '/property-tax-database': 'pro',
  '/community-intelligence': 'pro',
  '/checklist': 'pro',
  '/compare': 'pro',
  '/admin': 'enterprise',
  // '/admin/compliance': 'enterprise' // Removed due to missing dependencies
};

const TIER_HIERARCHY: Record<string, number> = {
  freemium: 0,
  pro: 1,
  investor: 2,
  agent_pro: 3,
  enterprise: 4
};

const getTierIcon = (tier: string) => {
  switch (tier) {
    case 'pro': return <Zap className="h-3 w-3" />;
    case 'investor': return <Crown className="h-3 w-3" />;
    case 'agent_pro': return <Crown className="h-3 w-3" />;
    case 'enterprise': return <Crown className="h-3 w-3" />;
    default: return <Lock className="h-3 w-3" />;
  }
};

export const SubscriptionAwareSidebarItem: React.FC<SubscriptionAwareSidebarItemProps> = ({
  item,
  isActive,
  onClick,
  children
}) => {
  const { subscription } = useEnhancedSubscription();
  const { hasRole } = useAuth();
  
  const requiredTier = TIER_REQUIREMENTS[item.path];
  const userTierLevel = TIER_HIERARCHY[subscription?.tier || 'freemium'];
  const requiredTierLevel = TIER_HIERARCHY[requiredTier || 'freemium'];
  
  // Admins have access to everything
  const hasAccess = hasRole(['admin', 'owner']) || !requiredTier || userTierLevel >= requiredTierLevel;
  const isLocked = !hasAccess;
  const isAdmin = hasRole(['admin', 'owner']);

  return (
    <div className={cn(
      'relative',
      isLocked && 'opacity-60'
    )}>
      {children}
      <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
        {isAdmin && (
          <Badge variant="outline" className="text-xs bg-red-50 border-red-200 text-red-700">
            <Shield className="h-3 w-3" />
            <span className="ml-1">Admin</span>
          </Badge>
        )}
        {requiredTier && !isAdmin && (
          <>
            {isLocked && (
              <Badge variant="outline" className="text-xs bg-orange-50 border-orange-200 text-orange-700">
                {getTierIcon(requiredTier)}
                <span className="ml-1 capitalize">{requiredTier}</span>
              </Badge>
            )}
            {hasAccess && requiredTier !== 'freemium' && (
              <Badge variant="outline" className="text-xs bg-green-50 border-green-200 text-green-700">
                ✓
              </Badge>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const SubscriptionStatusIndicator: React.FC = () => {
  const { subscription, loading } = useEnhancedSubscription();
  const { hasRole } = useAuth();

  if (loading) {
    return (
      <div className="px-3 py-2 mb-4 bg-gray-50 rounded-lg">
        <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
      </div>
    );
  }

  const isAdmin = hasRole(['admin', 'owner']);

  const tierColors = {
    freemium: 'bg-gray-100 text-gray-700 border-gray-300',
    pro: 'bg-blue-100 text-blue-700 border-blue-300',
    investor: 'bg-purple-100 text-purple-700 border-purple-300',
    agent_pro: 'bg-green-100 text-green-700 border-green-300',
    enterprise: 'bg-yellow-100 text-yellow-700 border-yellow-300'
  };

  return (
    <div className="px-3 py-2 mb-4 bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-900">Current Plan</span>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Badge variant="outline" className="text-xs bg-red-50 border-red-200 text-red-700">
              <Shield className="h-3 w-3 mr-1" />
              Admin
            </Badge>
          )}
          <Badge 
            variant="outline" 
            className={cn(
              'capitalize',
              tierColors[subscription?.tier || 'freemium']
            )}
          >
            {subscription?.tier || 'Freemium'}
          </Badge>
        </div>
      </div>
      {isAdmin ? (
        <p className="text-xs text-green-600 mt-1">
          Full access to all features
        </p>
      ) : subscription?.tier === 'freemium' ? (
        <p className="text-xs text-gray-500 mt-1">
          5 calculations remaining this month
        </p>
      ) : null}
    </div>
  );
};
